import { useCallback, useEffect, useState } from 'react';

import { GameReturnDto } from '@skillarea/lib-shared';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  useLocation,
  notFound,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import styles from './GameDetail.module.scss';

import { GameDetail } from '~/components/GameDetail/GameDetail';
import { DemoPlayModal } from '~/components/games/DemoPlayModal';
import {
  getLocalGameComponent,
  isLocalGameAvailable,
} from '~/components/games';
import { MatchmakingModal } from '~/components/Matchmaking/MatchmakingModal';
import { MinimumAgeModal } from '~/components/MinimumAgeModal/MinimumAgeModal';
import { MoreGames } from '~/components/MoreGames/MoreGames';
import { useMinimumAgeGuard } from '~/hooks/useMinimumAgeGuard';
import { useLocalizedGame } from '~/hooks/useLocalizedGame';
import { useBalance } from '~/stores/accountStore';
import { useIsLoggedIn } from '~/stores/onboardStore';
import { gameQueries } from '~/stores/gameStore';

function GameNotFoundContent() {
  const { t } = useTranslation('games');

  return (
    <div className={styles.notFound}>
      <div className={styles.notFoundContainer}>
        <h1 className={styles.notFoundTitle}>{t('ui.notFound.title')}</h1>
        <Link className={styles.backLink} to="/games">
          {t('ui.notFound.backLink')}
        </Link>
      </div>
    </div>
  );
}

function isLegacyNumericGameId(gameId: string): boolean {
  return /^\d+$/.test(gameId);
}

function resolveGameByRouteParam(
  games: GameReturnDto[],
  gameId: string,
): {
  game: GameReturnDto | undefined;
  isLegacyNumeric: boolean;
} {
  const normalizedGameId = gameId.toLowerCase();
  const gameBySlug = games.find(
    (candidateGame) => candidateGame.slug.toLowerCase() === normalizedGameId,
  );
  if (gameBySlug) {
    return {
      game: gameBySlug,
      isLegacyNumeric: false,
    };
  }

  if (!isLegacyNumericGameId(gameId)) {
    return {
      game: undefined,
      isLegacyNumeric: false,
    };
  }

  const numericId = Number(gameId);
  const gameByNumericId = games.find(
    (candidateGame) => candidateGame.id === numericId,
  );

  return {
    game: gameByNumericId,
    isLegacyNumeric: gameByNumericId !== undefined,
  };
}

export const Route = createFileRoute('/games/$gameId')({
  loader: async ({ context, params }) => {
    const opts = gameQueries.list({}, context.queryClient);
    const data = await context.queryClient.ensureQueryData(opts);
    const games = data?.items ? Object.values(data.items) : [];
    const resolvedGame = resolveGameByRouteParam(games, params.gameId);
    const game = resolvedGame.game;

    if (!game) {
      throw notFound();
    }

    if (resolvedGame.isLegacyNumeric) {
      throw redirect({
        to: '/games/$gameId',
        params: { gameId: game.slug },
        search: true,
        hash: true,
        replace: true,
      });
    }
  },
  notFoundComponent: GameNotFoundContent,
  component: GamePage,
});

function GamePage() {
  const { gameId } = Route.useParams();
  const { queryClient } = Route.useRouteContext();
  const { data } = useSuspenseQuery(gameQueries.list({}, queryClient));

  const allGames = data.items ? Object.values(data.items) : [];
  const resolvedGame = resolveGameByRouteParam(allGames, gameId);
  const game = resolvedGame.game;
  const isLoggedIn = useIsLoggedIn();
  const balanceCents = useBalance();
  const location = useLocation();
  const [showMatchmakingModal, setShowMatchmakingModal] = useState(false);
  const [showDemoPlay, setShowDemoPlay] = useState(false);
  const [pendingHashAfterLogin, setPendingHashAfterLogin] = useState<
    string | null
  >(null);
  const navigate = useNavigate();
  const {
    closeMinimumAgeModal,
    isMinimumAgeModalOpen,
    isUnderMinimumAge,
    minimumAge,
    runIfAgeAllowed,
    showMinimumAgeModal,
  } = useMinimumAgeGuard();

  useEffect(() => {
    if (!game || !resolvedGame.isLegacyNumeric) {
      return;
    }

    void navigate({
      to: '/games/$gameId',
      params: { gameId: game.slug },
      search: true,
      hash: true,
      replace: true,
    });
  }, [game, navigate, resolvedGame.isLegacyNumeric]);

  const isLegacyNumeric = resolvedGame.isLegacyNumeric;
  const gameRouteId = game?.slug ?? gameId;

  const openLoginModal = useCallback(() => {
    void navigate({
      to: '/games/$gameId',
      params: { gameId: gameRouteId },
      hash: 'login',
    });
  }, [gameRouteId, navigate]);

  useEffect(() => {
    const isPlayOrMatchmaking =
      location.hash === 'play' ||
      location.hash === '#play' ||
      location.hash === 'matchmaking' ||
      location.hash === '#matchmaking';
    if (!isPlayOrMatchmaking) {
      return;
    }

    if (!location.pathname.startsWith('/games/')) {
      return;
    }

    if (!isLoggedIn) {
      setPendingHashAfterLogin(location.hash.replace('#', ''));
      openLoginModal();
      return;
    }

    if (isUnderMinimumAge) {
      showMinimumAgeModal();
      return;
    }

    setShowMatchmakingModal(true);
  }, [
    isLoggedIn,
    isUnderMinimumAge,
    location.hash,
    location.pathname,
    openLoginModal,
    showMinimumAgeModal,
  ]);

  useEffect(() => {
    if (!isUnderMinimumAge || !showMatchmakingModal) {
      return;
    }

    setShowMatchmakingModal(false);
    showMinimumAgeModal();
  }, [isUnderMinimumAge, showMatchmakingModal, showMinimumAgeModal]);

  useEffect(() => {
    const isLoginHash = location.hash === 'login' || location.hash === '#login';

    if (pendingHashAfterLogin && !isLoggedIn && !isLoginHash) {
      setPendingHashAfterLogin(null);
    }
  }, [isLoggedIn, location.hash, pendingHashAfterLogin]);

  useEffect(() => {
    if (!pendingHashAfterLogin || !isLoggedIn) {
      return;
    }

    const hashToRestore = pendingHashAfterLogin;
    setPendingHashAfterLogin(null);
    void navigate({
      to: '/games/$gameId',
      params: { gameId: gameRouteId },
      hash: hashToRestore,
      replace: true,
    });
  }, [isLoggedIn, pendingHashAfterLogin, navigate, gameRouteId]);

  if (isLegacyNumeric) {
    return null;
  }

  const allGamesList = data ? Object.values(data.items) : [];
  const localizedGame = useLocalizedGame(
    game ?? {
      slug: gameId,
      name: '',
      description: '',
      rules: '',
    },
  );
  const GameComponent = game ? getLocalGameComponent(game.slug) : null;
  const canDemoPlay = game
    ? import.meta.env.VITE_DEMO_PLAY === 'true' &&
      isLocalGameAvailable(game.slug)
    : false;

  if (!game) {
    return <GameNotFoundContent />;
  }

  const closeDemo = () => setShowDemoPlay(false);

  const handleCloseMatchmaking = () => {
    setShowMatchmakingModal(false);

    const isPlayOrMatchmaking =
      location.hash === 'play' ||
      location.hash === '#play' ||
      location.hash === 'matchmaking' ||
      location.hash === '#matchmaking';
    if (!isPlayOrMatchmaking) {
      return;
    }

    void navigate({
      to: '/games/$gameId',
      params: { gameId: gameRouteId },
      hash: '',
      replace: true,
    });
  };

  return (
    <>
      <div className={styles.gameDetailPage}>
        <GameDetail
          {...game}
          onDemoPlay={
            canDemoPlay
              ? () =>
                  runIfAgeAllowed(() => {
                    setShowDemoPlay(true);
                  })
              : undefined
          }
        />
        <MoreGames
          currentGameSlug={game?.slug}
          games={allGamesList}
          isLoading={false}
        />
      </div>
      {showMatchmakingModal && (
        <MatchmakingModal
          game={game}
          gameMetadata={{
            playerCount: 110, // TODO: use real data
          }}
          onClose={handleCloseMatchmaking}
          userBalance={balanceCents}
        />
      )}
      {GameComponent && (
        <DemoPlayModal
          GameComponent={GameComponent}
          gameSlug={game.slug}
          isOpen={showDemoPlay}
          onClose={closeDemo}
          rules={localizedGame.rules}
          showTestPromotionButton={game.slug === 'classic-chess'}
          showTestSituationButtons={
            game.slug === 'battleship' ||
            game.slug === 'tic-tac-toe' ||
            game.slug === 'four-in-a-row'
          }
        />
      )}
      <MinimumAgeModal
        isOpen={isMinimumAgeModalOpen}
        minimumAge={minimumAge}
        onClose={closeMinimumAgeModal}
      />
    </>
  );
}
